<script setup lang="ts">
import { ref } from "vue";
import debounce from "lodash/debounce";

import AlertControl from "@/common/controls/AlertControl.vue";

import type { HotelDirectoryIndexViewModel } from "./types";
import { useHotelDirectoryAppBase } from "./HotelDirectoryAppBase";
import HotelDirectoryForm from "./components/HotelDirectoryForm.vue";
import HotelDirectoryList from "./components/HotelDirectoryList.vue";

const props = defineProps({
  viewModel: {
    type: Object as () => HotelDirectoryIndexViewModel,
    required: true,
  },
});

const { fetchHotelsCore, selection, page, sort, results, isLoading, error } = useHotelDirectoryAppBase(props.viewModel);

const formRef = ref<any>(null);
const listWrapperRef = ref<HTMLElement | null>(null);

const fetchHotels = debounce(fetchHotelsCore, 800, { leading: false, trailing: true });

function onFiltersChanged(payload) {
  selection.value = payload.selection;
  fetchHotels(payload.action);
}

function onSortChanged(value) {
  sort.value = value;
  fetchHotelsCore("sort");
}

function onPageChanged(value) {
  page.value = value;
  fetchHotelsCore("page").then(() => {
    listWrapperRef.value?.scrollIntoView({ behavior: "smooth" });
  });
}

function onClearFiltersOnEmptyResults() {
  formRef.value?.clearFilters?.();
}
</script>

<template>
  <div id="hotel-directory-app-vue3">
    <div class="hotel-search__title">{{ $res.Views.LocalHotelSearch_Title }}</div>
    <AlertControl v-if="error">{{ error }}</AlertControl>
    <div class="hotel-directory" data-qa="hotel-directory-app" ref="listWrapperRef">
      <HotelDirectoryForm
        ref="formRef"
        class="hotel-directory__filters"
        :countries="results.countries"
        :regions="results.regions"
        :locations="results.locations"
        :location-areas="results.locationAreas"
        :hotel-category-groups="results.hotelCategoryGroups"
        :hotel-attributes="results.hotelAttributes"
        :selection="selection"
        @selection-change="onFiltersChanged"
      />
      <div v-if="isLoading" class="hotel-directory__loader">
        <div class="busy-indicator"></div>
      </div>
      <HotelDirectoryList
        v-else
        class="hotel-directory__results"
        :hotels="results.orderedHotels"
        :hotel-attributes="results.hotelAttributes"
        :per-page="results.hotelsPerPage"
        :total="results.totalFound"
        :total-found-message-format="results.totalFoundMessageFormat"
        :page="page"
        :sort="sort"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @clear-filters="onClearFiltersOnEmptyResults"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
#hotel-directory-app-vue3 {
  min-width: 1128px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.hotel-search__title {
  font-size: 28px;
  line-height: 44px;
  font-weight: 600;
}

.hotel-directory {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.hotel-directory__filters {
  flex: 0 0 auto;
  width: 262px;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow);
}

.hotel-directory__loader {
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hotel-directory__results {
  flex: 1 1 0;
  min-width: 0;
}
</style>
